import { Constants, fetchIceServers } from "./ApiConstants";
import {
  WebRTCConnectionState,
  WebSocketConnectionState,
  setWebRTCDataChannelOpen,
  setWebRTCState,
  setWebSocketOpen,
  setWebSocketRtt,
  setWebSocketState,
} from "@store/connection/connectionSlice";
import { errorCheck, getDiffInSec } from "../CommonUtil";
import {
  getProductId,
  handleRelayReceipt,
  handleTestRunStateEvent,
  noteEyeTrackingSample,
  onMessageCallbacks,
} from "./WsUtil";
import { updateProductTs, updateStatus } from "../redux-utils/actions/productActions";

import { MessageOpcode } from "types/MessageOpcode";
import WebSocket from "isomorphic-ws";
import { clearSceneImage, processWebRTCMessage } from "./WebRTCUtil";
import { readIceServersHost, readSelectedCandidatePair } from "./webrtcStats";
import { store } from "@store/index";

// Connection states for better state management
const CONNECTION_STATES = {
  DISCONNECTED: "disconnected",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  RECONNECTING: "reconnecting",
};

// WebRTC connection states
const WEBRTC_STATES = {
  DISCONNECTED: "disconnected",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  FAILED: "failed",
};

const SCENE_CONTROL_HEARTBEAT_MS = 5000;
const MAX_PENDING_REMOTE_CANDIDATES = 50;

let sceneViewWanted = false;

export function buildSocketUrl(baseUrl, productId) {
  const url = new URL(baseUrl);
  url.searchParams.set("from", "web");
  url.searchParams.set("pid", productId);
  return url.toString();
}

class WebSocketService {
  // Private static instance
  static #instance = null;

  static peekInstance() {
    return WebSocketService.#instance;
  }

  // Singleton pattern implementation
  static getInstance(renewForced = false) {
    if (!WebSocketService.#instance) {
      WebSocketService.#instance = new WebSocketService();
    } else {
      const instance = WebSocketService.#instance;
      // Reconnect only sockets that are actually gone or stuck: an open
      // socket stays (killing it every 30s churned the connection and every
      // WebRTC handshake with it), and a connect attempt younger than 30s is
      // left to finish instead of being stampeded by concurrent callers.
      const socketOpen = instance.websocket?.readyState === WebSocket.OPEN;
      const connectingFresh =
        instance.websocket?.readyState === WebSocket.CONNECTING &&
        instance.lastInitTime &&
        getDiffInSec(instance.lastInitTime) <= 30;
      if (renewForced || (!socketOpen && !connectingFresh)) {
        instance.reconnect();
      }
    }
    return WebSocketService.#instance;
  }

  /* `declare` emits no runtime code; plain fields would get [[Define]] semantics
     under the esnext target and clobber the constructor's assignments. */
  declare websocket: WebSocket | null;
  declare isOpen: boolean;
  declare connectionState: string;
  declare msgToBeSentOnOpen: Record<string, any> | null;
  declare msgQueuedAt: number | null;
  declare intentionalClose: boolean;
  declare pingInterval: ReturnType<typeof setInterval> | null;
  declare reconnectTimeout: ReturnType<typeof setTimeout> | null;
  declare lastInitTime: Date | null;
  declare reconnectAttempts: number;
  declare maxReconnectAttempts: number;
  declare lastSeq: number;
  declare socketPid: string | null;
  declare hasEverConnected: boolean;
  declare lastMessageAt: number | null;
  declare peerConnection: RTCPeerConnection | null;
  declare dataChannel: RTCDataChannel | null;
  declare webrtcState: string;
  declare rtcConfiguration: RTCConfiguration | null;
  declare onWebRTCMessageCallback: ((...args: any[]) => void) | null;
  declare onWebRTCStateChangeCallback: ((...args: any[]) => void) | null;
  declare latestImageData: Record<string, any> | null;
  declare _peerConnectionCreation: Promise<unknown> | null;
  declare webrtcRetryTimer: ReturnType<typeof setTimeout> | null;
  declare webrtcConnectingSince: number | null;
  declare webrtcIceConnectedAt: number | null;
  declare webrtcStatsReported: string | null;
  declare webrtcIceServersHost: string | undefined;
  declare webrtcIceServersSource: string | undefined;
  declare webrtcAttemptId: number;
  declare pendingRemoteCandidates: { attemptId: number; candidateObj: RTCIceCandidateInit }[];
  declare sceneViewEnabled: boolean;
  declare sceneControlInterval: ReturnType<typeof setInterval> | null;

  constructor() {
    if (WebSocketService.#instance) {
      console.warn("WebSocketService constructor called more than once!");
    }

    this.websocket = null;
    this.isOpen = false;
    this.connectionState = CONNECTION_STATES.DISCONNECTED;
    this.msgToBeSentOnOpen = null;
    this.msgQueuedAt = null;
    this.intentionalClose = false;
    this.pingInterval = null;
    this.reconnectTimeout = null;
    this.lastInitTime = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    // Reliability protocol state. lastSeq tracks the newest backend sequence
    // number seen so a reconnect can ask for a replay of what was missed.
    // Legacy backends never stamp seq, so lastSeq stays -1 and no resume is
    // requested. Sequences are per device on the backend, so the state is
    // tied to socketPid and reset on device switch.
    this.lastSeq = -1;
    this.socketPid = null;
    this.hasEverConnected = false;
    this.lastMessageAt = null;

    // WebRTC related properties
    this.peerConnection = null;
    this.dataChannel = null;
    this.webrtcState = WEBRTC_STATES.DISCONNECTED;
    this.rtcConfiguration = null;
    this.onWebRTCMessageCallback = null;
    this.onWebRTCStateChangeCallback = null;
    this.latestImageData = null;
    this.webrtcRetryTimer = null;
    this.webrtcConnectingSince = null;
    this.webrtcIceConnectedAt = null;
    this.webrtcStatsReported = null;
    this.webrtcAttemptId = 0;
    this.pendingRemoteCandidates = [];
    this.sceneViewEnabled = sceneViewWanted;
    this.sceneControlInterval = null;

    // Initialize the connection
    this.connect();
  }

  connect() {
    // Don't create a new connection if one already exists and is open
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      console.log("WebSocket connection already open.");
      return;
    }

    const productId = getProductId();
    if (!productId) {
      this.cleanupConnection();
      this.connectionState = CONNECTION_STATES.DISCONNECTED;
      store.dispatch(setWebSocketState(WebSocketConnectionState.DISCONNECTED));
      return;
    }

    // Clean up any existing connection first
    this.cleanupConnection();

    this.intentionalClose = false;
    this.lastInitTime = new Date();
    this.connectionState = CONNECTION_STATES.CONNECTING;

    store.dispatch(setWebSocketState(WebSocketConnectionState.CONNECTING));

    this.initSocket(productId);
    this.setupPingInterval();
  }

  reconnect() {
    console.log("Forcing WebSocket reconnection");
    this.cleanupConnection();
    this.reconnectAttempts = 0;
    this.connect();
  }

  cleanupConnection() {
    if (this.websocket) {
      try {
        this.websocket.onopen = null;
        this.websocket.onmessage = null;
        this.websocket.onclose = null;
        this.websocket.onerror = null;
        this.websocket.close();
      } catch (e) {
        console.error("Error cleaning up WebSocket:", e);
      }
      this.websocket = null;
    }

    this.isOpen = false;

    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Also clean up WebRTC connection if it exists
    this.cleanupWebRTC();
  }

  setupPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }

    this.pingInterval = setInterval(() => {
      if (this.intentionalClose) {
        console.log("clearing ping interval intentionally closed");
        this.close();
        return;
      }
      if (this.websocket && this.isOpen) {
        this.sendPing();
      } else {
        console.log("Reconnecting WebSocket...");
        this.connect();
      }
    }, 10000);
  }

  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log(`Maximum reconnect attempts (${this.maxReconnectAttempts}) reached`);
      this.connectionState = CONNECTION_STATES.DISCONNECTED;

      store.dispatch(setWebSocketState(WebSocketConnectionState.DISCONNECTED));
      return;
    }

    this.connectionState = CONNECTION_STATES.RECONNECTING;

    store.dispatch(setWebSocketState(WebSocketConnectionState.RECONNECTING));

    // Calculate backoff time with exponential increase (capped at 30 seconds)
    const backoffTime = Math.min(30000, 2000 * Math.pow(1.5, this.reconnectAttempts));
    this.reconnectAttempts++;

    console.log(
      `Reconnecting in ${backoffTime}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`,
    );

    this.reconnectTimeout = setTimeout(() => {
      console.log("Executing reconnection attempt");
      this.connect();
    }, backoffTime);
  }

  // WebRTC related methods
  cleanupWebRTC() {
    this.webrtcAttemptId++;
    this.pendingRemoteCandidates = [];
    this.stopSceneControlHeartbeat();
    clearSceneImage();
    if (this.dataChannel) {
      try {
        this.dataChannel.onclose = null;
        this.dataChannel.close();
      } catch (e) {
        console.error("Error closing data channel:", e);
      }
      this.dataChannel = null;
    }

    if (this.peerConnection) {
      try {
        this.peerConnection.close();
      } catch (e) {
        console.error("Error closing peer connection:", e);
      }
      this.peerConnection = null;
    }

    this.webrtcState = WEBRTC_STATES.DISCONNECTED;
    this.updateWebRTCState();
  }

  ensureWebRTCConnection() {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) return;
    if (this.webrtcState === WEBRTC_STATES.CONNECTED) return;
    if (this.webrtcState === WEBRTC_STATES.CONNECTING) {
      // A CONNECTING state older than this is a dead handshake (e.g. the offer
      // was sent while the headset was unreachable) — discard and re-offer.
      const stuckMs = this.webrtcConnectingSince ? Date.now() - this.webrtcConnectingSince : 0;
      if (stuckMs <= 15000) return;
      this.reportWebRTCStats("timeout", "handshake-timeout");
    }
    if (this.peerConnection) this.cleanupWebRTC();
    this.initializeWebRTC();
  }

  scheduleWebRTCRetry() {
    if (this.webrtcRetryTimer) return;
    this.webrtcRetryTimer = setTimeout(() => {
      this.webrtcRetryTimer = null;
      this.ensureWebRTCConnection();
      // Keep polling until the channel is up: an offer sent while the headset
      // was offline gets no answer, so nothing event-driven would retry it.
      if (
        this.webrtcState !== WEBRTC_STATES.CONNECTED &&
        this.websocket &&
        this.websocket.readyState === WebSocket.OPEN
      ) {
        this.scheduleWebRTCRetry();
      }
    }, 10000);
  }

  async initializeWebRTC() {
    if (
      this.webrtcState === WEBRTC_STATES.CONNECTING ||
      this.webrtcState === WEBRTC_STATES.CONNECTED
    ) {
      console.log("WebRTC is already connecting or connected");
      return;
    }

    this.webrtcState = WEBRTC_STATES.CONNECTING;
    this.webrtcConnectingSince = Date.now();
    this.webrtcIceConnectedAt = null;
    this.webrtcStatsReported = null;
    // The attempt survives an await below; cleanupWebRTC bumps the id so a
    // superseded attempt aborts instead of overwriting the newer connection.
    const attemptId = ++this.webrtcAttemptId;
    this.updateWebRTCState();

    try {
      const iceServers = await fetchIceServers();
      if (attemptId !== this.webrtcAttemptId) return;
      const { iceServersHost, iceServersSource } = readIceServersHost(iceServers);
      this.webrtcIceServersHost = iceServersHost;
      this.webrtcIceServersSource = iceServersSource;
      this.rtcConfiguration = { iceServers };
      this.peerConnection = new RTCPeerConnection(this.rtcConfiguration);

      // Set up data channel
      this.dataChannel = this.peerConnection.createDataChannel("eyeImageChannel", {
        ordered: true,
      });

      this.dataChannel.onopen = () => {
        console.log("WebRTC Data Channel opened");
        this.webrtcState = WEBRTC_STATES.CONNECTED;
        this.updateWebRTCState();
        this.reportWebRTCStats("connected");
        this.sendSceneControl();
        this.startSceneControlHeartbeat();
      };

      this.dataChannel.onclose = () => {
        console.log("WebRTC Data Channel closed");
        this.stopSceneControlHeartbeat();
        clearSceneImage();
        this.webrtcState = WEBRTC_STATES.DISCONNECTED;
        this.updateWebRTCState();
        this.reportWebRTCStats("failed", "data-channel-closed");
      };

      this.dataChannel.onerror = (error) => {
        console.error("WebRTC Data Channel error:", error);
        this.stopSceneControlHeartbeat();
        clearSceneImage();
        this.webrtcState = WEBRTC_STATES.FAILED;
        this.updateWebRTCState();
        this.reportWebRTCStats("failed", "data-channel-error");
      };

      this.dataChannel.onmessage = (event) => {
        this.handleWebRTCMessage(event);
      };

      // Handle ICE candidates
      this.peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          this.sendWebRTCCandidate(event.candidate);
        }
      };

      this.peerConnection.oniceconnectionstatechange = () => {
        const state = this.peerConnection.iceConnectionState;
        console.log("ICE connection state:", state);
        if ((state === "connected" || state === "completed") && !this.webrtcIceConnectedAt) {
          this.webrtcIceConnectedAt = Date.now();
        }
        if (state === "failed" || state === "disconnected" || state === "closed") {
          this.webrtcState = WEBRTC_STATES.FAILED;
          this.updateWebRTCState();
        }
        if (state === "failed") {
          this.reportWebRTCStats("failed", "ice-failed");
        }
      };

      // Create offer and send it through WebSocket
      this.createAndSendOffer(attemptId);
    } catch (error) {
      console.error("Error initializing WebRTC:", error);
      if (attemptId !== this.webrtcAttemptId) return;
      this.webrtcState = WEBRTC_STATES.FAILED;
      this.updateWebRTCState();
      this.reportWebRTCStats("failed", "init-error");
    }
  }

  createAndSendOffer(attemptId) {
    const pc = this.peerConnection;
    pc.createOffer()
      .then((offer) => pc.setLocalDescription(offer))
      .then(() => {
        if (attemptId !== this.webrtcAttemptId) return;
        // Raw SDP string only — the headset reads data["sdp"] as the SDP text.
        const sent = this.sendWebRTCSignaling({
          type: "offer",
          sdp: pc.localDescription.sdp,
        });
        if (!sent) this.cleanupWebRTC();
      })
      .catch((error) => {
        console.error("Error creating offer:", error);
        if (attemptId !== this.webrtcAttemptId) return;
        this.webrtcState = WEBRTC_STATES.FAILED;
        this.updateWebRTCState();
        this.reportWebRTCStats("failed", "offer-error");
      });
  }

  handleWebRTCMessage(msg) {
    try {
      console.log("msg received on WebRTC data channel:", msg);
      processWebRTCMessage(msg);

      if (this.onWebRTCMessageCallback) {
        this.onWebRTCMessageCallback(msg);
      }
    } catch (error) {
      console.error("Error handling WebRTC message:", error);
    }
  }

  sendWebRTCSignaling(data) {
    if (this.websocket && this.isOpen) {
      this.sendMessage({ ...data, pid: getProductId() }, "webrtc-message");
      return true;
    }
    console.error("Cannot send WebRTC signaling: WebSocket not connected");
    return false;
  }

  async reportWebRTCStats(outcome, failureReason = undefined) {
    if (this.webrtcStatsReported === "connected" && outcome !== "connected") {
      outcome = "dropped";
    } else if (this.webrtcStatsReported) {
      return;
    }
    this.webrtcStatsReported = outcome;

    const startedAt = this.webrtcConnectingSince;
    const payload: Record<string, any> = {
      pid: getProductId(),
      role: "web",
      outcome,
      failureReason,
      iceConnectMs:
        this.webrtcIceConnectedAt && startedAt ? this.webrtcIceConnectedAt - startedAt : undefined,
      dataChannelOpenMs: outcome === "connected" && startedAt ? Date.now() - startedAt : undefined,
      iceServersHost: this.webrtcIceServersHost,
      iceServersSource: this.webrtcIceServersSource,
      clientVersion: process.env.REACT_APP_SENTRY_RELEASE,
    };

    try {
      Object.assign(payload, readSelectedCandidatePair(await this.peerConnection?.getStats()));
    } catch (error) {
      console.warn("Could not read WebRTC stats:", error);
    }

    if (this.websocket && this.isOpen) {
      this.sendMessage(payload, "webrtc-stats");
    }
  }

  sendWebRTCCandidate(candidate) {
    this.sendWebRTCSignaling({
      type: "candidate",
      candidate: candidate,
    });
  }

  handleWebRTCSignalingMessage(message) {
    const signal = message.data;
    switch (signal.type) {
      case "offer":
        this.handleOffer(signal);
        break;
      case "answer":
        this.handleAnswer(signal);
        break;
      case "candidate":
        this.handleCandidate(signal);
        break;
      default:
        console.warn("Unknown WebRTC signaling message type:", signal.type);
    }
  }

  async handleOffer(data) {
    try {
      if (this.dataChannel && this.dataChannel.readyState === "open") {
        console.log("Ignoring incoming WebRTC offer: data channel already open");
        return;
      }
      // A leftover peer connection here is either dead or holds our own
      // unanswered offer (glare with a headset-initiated offer). The headset's
      // offer wins: discard ours and answer theirs.
      if (this.peerConnection) this.cleanupWebRTC();
      this.webrtcState = WEBRTC_STATES.CONNECTING;
      this.webrtcConnectingSince = Date.now();
      this.updateWebRTCState();
      await this.createPeerConnection(false); // answerer
      await this.peerConnection.setRemoteDescription(
        new RTCSessionDescription({ type: "offer", sdp: data.sdp }),
      );
      await this.flushPendingRemoteCandidates();
      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);
      this.sendWebRTCSignaling({ type: "answer", sdp: answer.sdp });
    } catch (e) {
      console.error(`Error handling offer: ${e.message}`, e);
    }
  }

  async handleAnswer(data) {
    try {
      if (!this.peerConnection) await this.createPeerConnection();
      await this.peerConnection.setRemoteDescription(
        new RTCSessionDescription({ type: "answer", sdp: data.sdp }),
      );
      await this.flushPendingRemoteCandidates();
    } catch (e) {
      console.error(`Error handling answer: ${e.message}`, e);
    }
  }

  async handleCandidate(data) {
    const candidateObj =
      data.candidate && typeof data.candidate === "object"
        ? {
            candidate: data.candidate.candidate,
            sdpMid: data.candidate.sdpMid,
            sdpMLineIndex: data.candidate.sdpMLineIndex,
          }
        : {
            candidate: data.candidate,
            sdpMid: data.sdpMid,
            sdpMLineIndex: data.sdpMLineIndex,
          };
    if (!this.peerConnection?.remoteDescription) {
      if (this.pendingRemoteCandidates.length >= MAX_PENDING_REMOTE_CANDIDATES) {
        this.pendingRemoteCandidates.shift();
      }
      this.pendingRemoteCandidates.push({ attemptId: this.webrtcAttemptId, candidateObj });
      return;
    }
    await this.addRemoteCandidate(this.peerConnection, candidateObj);
  }

  async flushPendingRemoteCandidates() {
    const pc = this.peerConnection;
    const attemptId = this.webrtcAttemptId;
    const pending = this.pendingRemoteCandidates;
    this.pendingRemoteCandidates = [];
    for (const entry of pending) {
      if (!pc || attemptId !== this.webrtcAttemptId) return;
      if (entry.attemptId !== attemptId) continue;
      await this.addRemoteCandidate(pc, entry.candidateObj);
    }
  }

  async addRemoteCandidate(pc, candidateObj) {
    try {
      await pc.addIceCandidate(new RTCIceCandidate(candidateObj));
    } catch (e) {
      console.error(`Error adding received ice candidate: ${e.message}`, e);
    }
  }

  // Public WebRTC methods
  reconnectWebRTC() {
    this.cleanupWebRTC();
    this.initializeWebRTC();
  }

  getLatestImageData() {
    return this.latestImageData;
  }

  getWebRTCState() {
    return this.webrtcState;
  }

  setWebRTCMessageCallback(callback) {
    this.onWebRTCMessageCallback = callback;
  }

  setWebRTCStateChangeCallback(callback) {
    this.onWebRTCStateChangeCallback = callback;
  }

  updateWebRTCState() {
    let state;
    switch (this.webrtcState) {
      case WEBRTC_STATES.CONNECTED:
        state = WebRTCConnectionState.CONNECTED;
        break;
      case WEBRTC_STATES.CONNECTING:
        state = WebRTCConnectionState.CONNECTING;
        break;
      case WEBRTC_STATES.FAILED:
        state = WebRTCConnectionState.FAILED;
        break;
      default:
        state = WebRTCConnectionState.DISCONNECTED;
    }

    // Update the Redux state with WebRTC state
    store.dispatch(setWebRTCState(state));
    store.dispatch(setWebRTCDataChannelOpen(this.webrtcState === WEBRTC_STATES.CONNECTED));

    // Keep the callback for backward compatibility
    if (this.onWebRTCStateChangeCallback) {
      this.onWebRTCStateChangeCallback(this.webrtcState);
    }

    if (
      this.webrtcState !== WEBRTC_STATES.CONNECTED &&
      this.websocket &&
      this.websocket.readyState === WebSocket.OPEN
    ) {
      this.scheduleWebRTCRetry();
    }
  }

  sendWebRTCMessage(data) {
    if (this.dataChannel && this.dataChannel.readyState === "open") {
      const message = typeof data === "string" ? data : JSON.stringify(data);
      this.dataChannel.send(message);
      return true;
    } else {
      console.error("Cannot send message: WebRTC data channel not connected");
      return false;
    }
  }

  /**
   *  Set up WebSocket connection for a new user and
   *  basic listeners to handle events
   */
  initSocket = (productId) => {
    if (productId !== this.socketPid) {
      // Backend sequence numbers are per device: a seq carried over from the
      // previous device would make the resume replay from the wrong position.
      // Treat the new device as a first connect (no resume).
      this.socketPid = productId;
      this.lastSeq = -1;
      this.lastMessageAt = null;
      this.hasEverConnected = false;
    }
    this.createWebsocket(buildSocketUrl(Constants.instance.WEBSOCKET_API_URL, productId));
  };

  createWebsocket(url) {
    console.log("Creating WebSocket connection:", url);
    this.websocket = new WebSocket(url);
    this.websocket.onopen = this.onConnOpen;
    this.websocket.onmessage = this.onMessage;
    this.websocket.onclose = this.onConnClose;
    this.websocket.onerror = this.onConnError;
  }

  /**
   * Drops any command queued while offline. Called when the flow that queued
   * it gives up (e.g. the start screen shows its failure dialog), so a later
   * reconnect cannot fire a ghost command.
   */
  clearQueuedMessage() {
    if (this.msgToBeSentOnOpen) {
      console.warn("Clearing queued WebSocket message:", this.msgToBeSentOnOpen.opcode);
    }
    this.msgToBeSentOnOpen = null;
    this.msgQueuedAt = null;
  }

  sendPing() {
    console.log("Sending WebSocket ping");
    // t is echoed back by newer backends so RTT can be measured; legacy
    // backends ignore it.
    this.sendMessage({ opcode: MessageOpcode.NOTHING, pid: getProductId(), t: Date.now() }, "ping");
  }

  /**
   *  Show connection status to us in the log
   */
  onConnOpen = () => {
    this.isOpen = true;
    this.connectionState = CONNECTION_STATES.CONNECTED;
    this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
    store.dispatch(setWebSocketState(WebSocketConnectionState.CONNECTED));
    store.dispatch(setWebSocketOpen(true));

    console.log("WebSocket connected successfully");

    // On reconnect, ask the backend to replay device-bound messages we missed
    // and to push the current test session snapshot. Legacy backends have no
    // 'resume' handler and simply ignore this. Backend seq counters are
    // per device, and lastSeq tracks the stream of socketPid — resume must
    // name that pid, not whichever product the UI currently selects.
    if (this.hasEverConnected && this.socketPid) {
      this.sendMessage({ pid: this.socketPid, lastSeq: this.lastSeq }, "resume");
    }
    this.hasEverConnected = true;

    if (this.msgToBeSentOnOpen) {
      // A queued command is only meaningful for a short while; firing a stale
      // test start minutes later would start a "ghost" test.
      const age = this.msgQueuedAt ? Date.now() - this.msgQueuedAt : 0;
      if (age <= 15000) {
        this.sendMessage(this.msgToBeSentOnOpen);
      } else {
        console.warn("Dropped stale queued WebSocket message (age ms):", age);
      }
      this.msgToBeSentOnOpen = null;
      this.msgQueuedAt = null;
    }

    // Open the WebRTC channel for the whole device connection, not just tests:
    // the headset streams its scene view whenever the channel is up. Headsets
    // without a passive WebRTC responder ignore this offer and keep initiating
    // themselves at test start (handleOffer answers them). A pending handshake
    // is tied to the socket it was signaled over, so anything short of an
    // established channel is dead after a (re)connect — restart it.
    if (this.webrtcState !== WEBRTC_STATES.CONNECTED) {
      this.cleanupWebRTC();
      this.initializeWebRTC();
    }
  };

  /**
   * Handle WebSocket errors
   */
  onConnError = (error) => {
    console.error("WebSocket error:", error);
    // We don't need to do anything here as onclose will be called after this
  };

  /**
   *  Log lost connection for now
   */
  onConnClose = (event) => {
    console.log(`WebSocket closed: ${event?.code} - ${event?.reason || "No reason provided"}`);
    this.isOpen = false;
    store.dispatch(updateStatus(MessageOpcode.NOTHING));
    store.dispatch(setWebSocketOpen(false));

    if (this.intentionalClose) {
      this.connectionState = CONNECTION_STATES.DISCONNECTED;
      store.dispatch(setWebSocketState(WebSocketConnectionState.DISCONNECTED));
      return; // Don't reconnect if closed intentionally
    }
    store.dispatch(setWebSocketState(WebSocketConnectionState.RECONNECTING));

    // Attempt reconnection
    this.attemptReconnect();
  };

  /**
   *  Used by application to send message to the WebSocket API Gateway
   *  @param message String message
   * @param event
   */
  sendMessage = (message, event = "onMessage") => {
    const wsm = {
      event,
      data: message,
    };
    const wsStr = JSON.stringify(wsm);
    console.log("Sending message. \nEvent:", wsm.event, "\nData:", wsm.data);

    if (this.websocket && this.isOpen) {
      this.websocket.send(wsStr);
    } else {
      // Retries of the same logical command (same opcode + uuid) keep the
      // FIRST enqueue time, so the on-open TTL measures from the original
      // attempt and a long-stale start cannot fire after the UI gave up.
      const queued = this.msgToBeSentOnOpen;
      const isSameCommand =
        queued && queued.opcode === message.opcode && queued.uuid === message.uuid;
      this.msgToBeSentOnOpen = message;
      if (!isSameCommand || !this.msgQueuedAt) {
        this.msgQueuedAt = Date.now();
      }
      console.error("WebSocket connection not found, message will be sent when connected");

      // Try to reconnect if not already connecting or reconnecting
      if (this.connectionState === CONNECTION_STATES.DISCONNECTED) {
        this.connect();
      }
    }
  };

  /**
   * Handler that receives the actual messages from the WebSocket API
   * @param data Message body received from WebSocket
   */
  onMessage = (data) => {
    if (data) {
      // Some legacy peers/intermediaries answer keepalives with a raw frame.
      if (data.data === "pong") {
        console.log("Received raw pong");
        return;
      }

      const message = errorCheck(() => JSON.parse(data.data));
      if (!message) {
        return;
      }

      // Reliability protocol events from newer backends.
      if (message.event === "pong") {
        if (typeof message.t === "number") {
          store.dispatch(setWebSocketRtt(Date.now() - message.t));
        }
        return;
      }
      if (message.event === "relay-delivered" || message.event === "relay-failed") {
        handleRelayReceipt(message);
        return;
      }
      if (message.event === "test-run-state") {
        handleTestRunStateEvent(message.testRun);
        return;
      }
      if (message.event === "resume-complete") {
        console.log("WebSocket resume complete, replayed messages:", message.replayed);
        return;
      }

      // Handle WebRTC signaling messages
      if (message.event === "webrtc-message") {
        this.handleWebRTCSignalingMessage(message);
        return;
      }

      // Track the backend's replay sequence number (absent on legacy backends).
      if (typeof message.seq === "number") {
        this.lastSeq = message.seq;
      }

      // Handle regular WebSocket messages
      if (message.opcode) {
        // Tracks live test/device traffic only (not reliability events), so
        // test-run-state reconciliation can tell a quiet UI from an active one.
        this.lastMessageAt = Date.now();
        const parsedOpcode = parseInt(message.opcode);

        if (onMessageCallbacks.has(parsedOpcode)) {
          onMessageCallbacks.get(parsedOpcode)(message);
          store.dispatch(updateProductTs(new Date().getTime()));
        }
        noteEyeTrackingSample(message);
      }
    }
  };

  /**
   * Close the WebSocket connection intentionally
   */
  close() {
    console.log("Intentionally closing WebSocket service");
    this.intentionalClose = true;
    if (this.webrtcRetryTimer) {
      clearTimeout(this.webrtcRetryTimer);
      this.webrtcRetryTimer = null;
    }
    this.connectionState = CONNECTION_STATES.DISCONNECTED;
    this.cleanupConnection();
  }

  async sendOffer() {
    try {
      await this.createPeerConnection(true); // true = offerer
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);
      this.sendWebRTCSignaling({
        type: "offer",
        sdp: this.peerConnection.localDescription.sdp,
      });
    } catch (error) {
      console.error("Error creating offer:", error);
      this.webrtcState = WEBRTC_STATES.FAILED;
      this.updateWebRTCState();
    }
  }

  createPeerConnection(isOfferer = false) {
    if (this.peerConnection) return Promise.resolve();
    if (this._peerConnectionCreation) return this._peerConnectionCreation;
    this._peerConnectionCreation = this._createPeerConnectionImpl(isOfferer).finally(() => {
      this._peerConnectionCreation = null;
    });
    return this._peerConnectionCreation;
  }

  async _createPeerConnectionImpl(isOfferer) {
    this.webrtcAttemptId++;
    this.rtcConfiguration = { iceServers: await fetchIceServers() };
    const pc = new RTCPeerConnection(this.rtcConfiguration);
    if (isOfferer) {
      this.dataChannel = pc.createDataChannel("eyeImageChannel", { ordered: true });
      this.setupDataChannelEvents(this.dataChannel);
    } else {
      pc.ondatachannel = (event) => {
        this.dataChannel = event.channel;
        this.setupDataChannelEvents(this.dataChannel);
      };
    }
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.sendWebRTCCandidate(event.candidate);
      }
    };
    pc.oniceconnectionstatechange = () => {
      if (["failed", "disconnected", "closed"].includes(pc.iceConnectionState)) {
        this.webrtcState = WEBRTC_STATES.FAILED;
        this.updateWebRTCState();
      }
    };
    this.peerConnection = pc;
  }

  setSceneViewEnabled(enabled) {
    this.sceneViewEnabled = !!enabled;
    this.sendSceneControl();
  }

  sendSceneControl() {
    if (!this.dataChannel || this.dataChannel.readyState !== "open") return;
    try {
      this.dataChannel.send(
        JSON.stringify({ kind: "scene-control", enabled: this.sceneViewEnabled }),
      );
    } catch (e) {
      console.error("Failed to send scene-control message:", e);
    }
  }

  startSceneControlHeartbeat() {
    this.stopSceneControlHeartbeat();
    this.sceneControlInterval = setInterval(() => {
      if (!this.dataChannel || this.dataChannel.readyState !== "open") {
        this.stopSceneControlHeartbeat();
        return;
      }
      this.sendSceneControl();
    }, SCENE_CONTROL_HEARTBEAT_MS);
  }

  stopSceneControlHeartbeat() {
    if (this.sceneControlInterval) {
      clearInterval(this.sceneControlInterval);
      this.sceneControlInterval = null;
    }
  }

  setupDataChannelEvents(dataChannel) {
    dataChannel.onopen = () => {
      this.webrtcState = WEBRTC_STATES.CONNECTED;
      this.updateWebRTCState();
      this.sendSceneControl();
      this.startSceneControlHeartbeat();
    };
    dataChannel.onclose = () => {
      this.stopSceneControlHeartbeat();
      clearSceneImage();
      this.webrtcState = WEBRTC_STATES.DISCONNECTED;
      this.updateWebRTCState();
    };
    dataChannel.onerror = (_error) => {
      this.stopSceneControlHeartbeat();
      clearSceneImage();
      this.webrtcState = WEBRTC_STATES.FAILED;
      this.updateWebRTCState();
    };
    dataChannel.onmessage = (event) => {
      this.handleWebRTCMessage(event);
    };
  }
}

// Export functions to interact with the singleton
export const getWSService = () => WebSocketService.getInstance();
export const getWSServiceForce = () => WebSocketService.getInstance(true);
export const sendOffer = () => WebSocketService.getInstance().sendOffer();
export const closeWSService = () => {
  const instance = WebSocketService.getInstance();
  instance.close();
};

export const rebindOpenSocket = () => {
  const instance = WebSocketService.peekInstance();
  const readyState = instance?.websocket?.readyState;
  if (readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING) {
    instance.reconnect();
  }
};

// A headset ignores offers while the previous device's channel is still open.
export const rebindSocketToSelectedDevice = () => {
  const instance = WebSocketService.peekInstance();
  if (!instance?.websocket || instance.socketPid === getProductId()) return;
  instance.reconnect();
};

// WebRTC specific exports
export const initWebRTC = () => {
  const instance = WebSocketService.getInstance();
  instance.initializeWebRTC();
  return instance;
};

export const reconnectWebRTC = () => {
  const instance = WebSocketService.getInstance();
  instance.reconnectWebRTC();
  return instance;
};

export const setSceneViewEnabled = (enabled) => {
  sceneViewWanted = !!enabled;
  WebSocketService.peekInstance()?.setSceneViewEnabled(sceneViewWanted);
};

export const getWebRTCState = () => {
  const instance = WebSocketService.getInstance();
  return instance.getWebRTCState();
};

if (import.meta.env.DEV && typeof window !== "undefined") {
  (window as unknown as Record<string, unknown>).simulateEyeTrackingReport = (
    startUuid?: string,
  ) => {
    const state = store.getState();
    const uuid = startUuid ?? state.test?.testId ?? null;
    getWSService().onMessage({
      data: JSON.stringify({
        opcode: "223",
        healthy: false,
        recoveryAttempts: 1,
        reason: "zero_values",
        scene: "dev-simulation",
        startUuid: uuid,
      }),
    });
    return {
      sentStartUuid: uuid,
      activeTestId: state.test?.testId ?? null,
      testCategory: state.test?.configuration?.name ?? null,
      testStatus: state.test?.status ?? null,
      promptRaised: store.getState().connection?.eyeTrackingFailure !== null,
    };
  };
}
